/**
 * wireMpStubDebug — agent smoke + player duo join for P3-41/42 WS stub.
 * Requires `pnpm mp:stub` (default ws://127.0.0.1:9998).
 *
 * Duo (two browsers / normal + incognito):
 *   1) pnpm mp:stub
 *   2) Both: Connect MP → Join room (same room id) → Lock-in
 * Solo stub match: Connect → Join → Lock-in + bot
 */

import { createMpClient } from '../../application/net/mpClient.js';
import { resolveMpWsUrl } from '../net/resolveMpWsUrl.js';

const DEFAULT_URL = resolveMpWsUrl(import.meta.env);
const DEFAULT_ROOM = 'duel';
const DEFAULT_LOADOUT = {
  championId: 'ashe',
  spell1: 'flash',
  spell2: 'ignite',
  runePage: 'standard_ad',
  masteryPage: 'offense_21_9_0'
};

function setStatus(text) {
  const el = document.getElementById('mp-stub-status');
  if (el) el.textContent = text;
}

export function wireMpStubDebug(globalObj = window) {
  let client = null;

  async function connect(opts = {}) {
    if (client) client.close();
    const url = opts.url || DEFAULT_URL;
    client = createMpClient({
      url,
      displayName: opts.displayName || `Player-${Math.floor(Math.random() * 900 + 100)}`,
      openSocket: (u) => new WebSocket(u || url)
    });
    client.onRoomState((room) => {
      const n = room?.players?.length || 0;
      const names = (room?.players || []).map((p) => p.displayName || p.clientId).join(', ');
      setStatus(`Room ${room.roomId} (${n}): ${names} · phase=${room.phase}`);
    });
    const welcome = await client.connect();
    setStatus(`Connected as ${welcome.payload.clientId}`);
    return {
      clientId: welcome.payload.clientId,
      status: client.getStatus()
    };
  }

  async function ping() {
    if (!client) throw new Error('not_connected');
    const pong = await client.requestPing();
    return pong.payload;
  }

  function close() {
    if (client) client.close();
    client = null;
    setStatus('Disconnected');
    return { status: 'closed' };
  }

  async function joinRoom(opts = {}) {
    if (!client) throw new Error('not_connected');
    const payload = {
      roomId: opts.roomId || DEFAULT_ROOM,
      mapId: opts.mapId,
      fillBots: opts.fillBots,
      displayName: opts.displayName
    };
    await client.joinRoom(payload);
    return client.getRoomState();
  }

  function lockIn(loadout = {}) {
    if (!client) throw new Error('not_connected');
    client.lockIn({ ...DEFAULT_LOADOUT, ...loadout });
    return client.getRoomState();
  }

  async function joinSharedRoom(opts = {}) {
    await connect({ displayName: opts.displayName });
    await joinRoom({ roomId: opts.roomId || DEFAULT_ROOM, mapId: opts.mapId });
    return client.getRoomState();
  }

  async function startSoloWithBot(opts = {}) {
    await connect({ displayName: opts.displayName || 'Solo' });
    await joinRoom({ roomId: opts.roomId || DEFAULT_ROOM, fillBots: true });
    lockIn({ ...DEFAULT_LOADOUT, ...opts.loadout, fillBots: true });
    const match = await client.waitForMatchStart();
    setStatus(`Solo+bot match_start · ${match.players.length} players on ${match.mapId}`);
    return {
      room: client.getRoomState(),
      match
    };
  }

  function bindUi() {
    const connectBtn = document.getElementById('mp-connect-btn');
    const joinBtn = document.getElementById('mp-join-btn');
    const lockBtn = document.getElementById('mp-lock-btn');
    const botBtn = document.getElementById('mp-bot-btn');
    const roomInput = document.getElementById('mp-room-id');
    const roomId = () => (roomInput && roomInput.value.trim()) || DEFAULT_ROOM;

    if (connectBtn) {
      connectBtn.addEventListener('click', () => {
        connect().catch((err) => setStatus(`Connect failed: ${err.message} (run pnpm mp:stub)`));
      });
    }
    if (joinBtn) {
      joinBtn.addEventListener('click', () => {
        joinRoom({ roomId: roomId() }).catch((err) => setStatus(`Join failed: ${err.message}`));
      });
    }
    if (lockBtn) {
      lockBtn.addEventListener('click', () => {
        try {
          lockIn();
          setStatus('Locked — waiting for match_start (2nd player or use Solo+bot)…');
          client.waitForMatchStart()
            .then((match) => {
              setStatus(`match_start · ${match.players.length} on ${match.mapId}`);
            })
            .catch((err) => {
              setStatus(`Still waiting: ${err.message}`);
            });
        } catch (err) {
          setStatus(`Lock-in failed: ${err.message}`);
        }
      });
    }
    if (botBtn) {
      botBtn.addEventListener('click', () => {
        startSoloWithBot({ roomId: roomId() })
          .then((r) => setStatus(
            `Solo+bot match_start · ${r.match.players.length} on ${r.match.mapId}`
          ))
          .catch((err) => setStatus(`Bot match failed: ${err.message} (run pnpm mp:stub)`));
      });
    }
  }

  bindUi();

  globalObj.__debugMpStub = {
    connect,
    ping,
    joinRoom,
    joinSharedRoom,
    startSoloWithBot,
    lockIn,
    sendMove: (x, z) => {
      if (!client) throw new Error('not_connected');
      return client.sendMove(x, z);
    },
    close,
    getRoomState: () => (client ? client.getRoomState() : null),
    getSnapshot: () => (client ? client.getSnapshot() : null),
    getMatchStart: () => (client ? client.getMatchStart() : null),
    DEFAULT_URL,
    DEFAULT_ROOM
  };
  return globalObj.__debugMpStub;
}
